import type { TrackingStatusDto } from '@miyuki/types';
import { Button } from '@miyuki/ui';
import { CheckIcon, ListOrderedIcon, Redo2Icon, RotateCcwIcon, Undo2Icon } from 'lucide-react';

import { t } from '@/lib/i18n';

/**
 * Franja inferior del modo seguimiento: dónde voy y qué confirmo.
 *
 * Reemplaza a `BottomBar` porque en seguimiento no se pinta: la paleta y el
 * borrador no tienen nada que ofrecer (RF-10). El botón de completar ocupa el
 * ancho porque es el único que se pulsa cien veces seguidas, muchas veces sin
 * mirar y con una sola mano (RNF-03).
 */
export interface TrackingBarProps {
  readonly status: TrackingStatusDto;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly onCompleteRow: () => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onGoToRow: () => void;
  readonly onReset: () => void;
}

export function TrackingBar({
  status,
  canUndo,
  canRedo,
  onCompleteRow,
  onUndo,
  onRedo,
  onGoToRow,
  onReset,
}: TrackingBarProps) {
  const percent = Math.round(status.ratio * 100);
  const position =
    status.currentRow === null
      ? t('trackingFinished')
      : t('trackingPosition', { n: status.currentRow + 1, total: status.totalRows });

  return (
    <footer className="bg-background/90 border-border shrink-0 border-t px-3 pb-3 pt-2 backdrop-blur">
      {/*
        La posición vive en una región viva porque quien teje no está mirando la
        pantalla: cambiar de fila tiene que anunciarse solo, sin ir a buscarlo
        (RNF-10). Es la primera región viva de la aplicación.
      */}
      <div
        role="status"
        aria-live="polite"
        className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5"
      >
        <span className="text-base font-medium">{position}</span>
        <span className="text-muted-foreground text-sm tabular-nums">
          {t('trackingPercent', { n: percent })}
        </span>
        <span className="text-muted-foreground w-full text-xs">
          {status.finished
            ? t('trackingFinishedBody', { total: status.totalRows })
            : `${
                status.rowsRemaining === 1
                  ? t('trackingRowsRemainingOne')
                  : t('trackingRowsRemaining', { n: status.rowsRemaining })
              } · ${t('trackingBeadsRemaining', { n: status.beadsRemaining })}`}
        </span>
      </div>

      {/*
        Los controles que cambian de fila van arriba y separados del de
        completar: una fila de más por error cuesta desensartar (RF-52).
      */}
      <div className="mb-2 flex items-center gap-1">
        <Button
          variant="ghost"
          size="touch-icon"
          aria-label={t('trackingUndo')}
          disabled={!canUndo}
          onClick={onUndo}
        >
          <Undo2Icon />
        </Button>
        <Button
          variant="ghost"
          size="touch-icon"
          aria-label={t('trackingRedo')}
          disabled={!canRedo}
          onClick={onRedo}
        >
          <Redo2Icon />
        </Button>
        <Button
          variant="ghost"
          size="touch-icon"
          aria-label={t('trackingGoToRow')}
          onClick={onGoToRow}
        >
          <ListOrderedIcon />
        </Button>
        <Button variant="ghost" size="touch-icon" aria-label={t('trackingReset')} onClick={onReset}>
          <RotateCcwIcon />
        </Button>
      </div>

      {status.currentRow !== null && (
        <Button size="touch" className="h-14 w-full text-base" onClick={onCompleteRow}>
          <CheckIcon />
          {t('trackingCompleteRow')}
        </Button>
      )}
    </footer>
  );
}
